import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { Stock } from '../../types';
import { StockNameManager } from '../../services/stockNameManager';
import {
  getTrendColorSet,
  getTrendTextColor,
} from '../../theme/taiwanTrendColors';

interface TopStocksRankingProps {
  stocks: Stock[];
  stateStocks?: Stock[]; // 新增 state.stocks 參數，用於獲取優化的股票名稱
}

const TopStocksRanking: React.FC<TopStocksRankingProps> = ({ stocks, stateStocks = [] }) => {
  const gainHeaderColor = getTrendTextColor(1);
  const lossHeaderColor = getTrendTextColor(-1);
  return (
    <Paper sx={{ p: 3, height: "700px" }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        熱門股票排行榜
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        今日漲跌幅最大的股票
      </Typography>
      <Box sx={{ height: "85%", overflow: "auto" }}>
        {/* 響應式佈局：寬螢幕用兩欄，窄螢幕用單欄 */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: { xs: 3, lg: 4 },
          height: '100%'
        }}>
          {/* Top Gainers */}
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                color: gainHeaderColor,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              <TrendingUp sx={{ fontSize: 20 }} />
              今日漲幅榜
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {stocks
                .filter((stock) => stock.changePercent > 0)
                .sort((a, b) => b.changePercent - a.changePercent)
                .slice(0, 5)
                .map((stock, index) => {
                  const colorSet = getTrendColorSet(stock.changePercent);
                  const textColor = getTrendTextColor(stock.changePercent);
                  return (
                    <Box
                      key={stock.symbol}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: { xs: 1.2, lg: 1.5 },
                        bgcolor: colorSet.light,
                        borderRadius: 1,
                        border: `1px solid ${colorSet.border}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          sx={{
                            bgcolor: colorSet.primary,
                            color: "white",
                            minWidth: 28,
                            fontSize: "0.7rem",
                          }}
                        />
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ fontSize: { xs: "0.95rem", md: "1rem" } }}
                          >
                            {stock.symbol}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ 
                              fontSize: { xs: "0.8rem", md: "0.85rem" },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '120px'
                            }}
                          >
                            {StockNameManager.getStockName(stock.symbol)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "0.85rem", lg: "0.9rem" } }}
                        >
                          ${stock.currentPrice}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ fontSize: "0.8rem", color: textColor }}
                        >
                          +{stock.changePercent.toFixed(2)}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>

          {/* Top Losers */}
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                color: lossHeaderColor,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              <TrendingDown sx={{ fontSize: 20 }} />
              今日跌幅榜
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {stocks
                .filter((stock) => stock.changePercent < 0)
                .sort((a, b) => a.changePercent - b.changePercent)
                .slice(0, 5)
                .map((stock, index) => {
                  const colorSet = getTrendColorSet(stock.changePercent);
                  const textColor = getTrendTextColor(stock.changePercent);
                  return (
                    <Box
                      key={stock.symbol}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: { xs: 1.2, lg: 1.5 },
                        bgcolor: colorSet.light,
                        borderRadius: 1,
                        border: `1px solid ${colorSet.border}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          sx={{
                            bgcolor: colorSet.primary,
                            color: "white",
                            minWidth: 28,
                            fontSize: "0.7rem",
                          }}
                        />
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ fontSize: { xs: "0.95rem", md: "1rem" } }}
                          >
                            {stock.symbol}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ 
                              fontSize: { xs: "0.8rem", md: "0.85rem" },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '120px'
                            }}
                          >
                            {StockNameManager.getStockName(stock.symbol)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ fontSize: { xs: "0.85rem", lg: "0.9rem" } }}
                        >
                          ${stock.currentPrice}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ fontSize: "0.8rem", color: textColor }}
                        >
                          {stock.changePercent.toFixed(2)}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default TopStocksRanking;
